import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { StayFields } from "./StayFields";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inp = "w-full mt-2 px-4 py-3 bg-[#F5F3ED] border border-[#E5E1D8] rounded-md focus:outline-none focus:border-[#8C6C50]";
const lbl = "text-xs tracking-[0.2em] uppercase font-semibold text-[#666]";

export const MultiRequestDialog = ({ open, onOpenChange, selected, toggle, onSent }) => {
  const items = Object.values(selected);
  const minDur = Math.max(1, ...items.map((l) => l.min_duration_months || 1));
  const [form, setForm] = useState({ arrival_date: "", departure_date: "", guests: 1, message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(
        `${API}/reservations`,
        { listing_ids: items.map((l) => l.id), ...form, guests: Number(form.guests) },
        { withCredentials: true }
      );
      toast.success(`Demande envoyée pour ${items.length} annonce${items.length > 1 ? "s" : ""}`);
      setForm({ arrival_date: "", departure_date: "", guests: 1, message: "" });
      onSent();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="multi-request-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Une demande pour {items.length} annonce{items.length > 1 ? "s" : ""}</DialogTitle>
          <DialogDescription>Une seule demande groupée, envoyée directement à chaque résidence concernée.</DialogDescription>
        </DialogHeader>
        <ul className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="multi-request-list">
          {items.map((l) => (
            <li key={l.id} className="flex items-center justify-between gap-2 text-sm bg-[#F5F3ED] rounded px-3 py-2">
              <span className="truncate">{l.title} <span className="text-[#666]">· {l.city}</span></span>
              <button type="button" onClick={() => toggle(l)} className="text-[#666] hover:text-[#1A1A1A]" aria-label="Retirer" data-testid={`multi-request-remove-${l.id}`}><X className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <StayFields form={form} setForm={setForm} listings={items} minMonths={minDur} testid="multi-request" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}>Personnes</label>
              <input type="number" required min={1} value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} className={inp} data-testid="multi-request-guests" />
            </div>
          </div>
          <div>
            <label className={lbl}>Message (optionnel)</label>
            <textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inp} placeholder="Précisez votre contexte, flexibilité, besoins particuliers…" data-testid="multi-request-message" />
          </div>
          <button type="submit" disabled={busy || !items.length} className="btn-primary w-full py-3.5 rounded-full text-sm font-medium disabled:opacity-60" data-testid="multi-request-submit">
            {busy ? "Envoi…" : "Envoyer ma demande"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
